import { useId, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Info, Network, Pencil } from 'lucide-react';
import type { OrgUnitDto } from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, cn, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { PageHeader } from '../../../shared/ui/PageHeader.js';
import { Select } from '../../../shared/ui/Select.js';
import { useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { orgApi, type OrgUnitNode } from '../api/org.api.js';
import { OrgTree } from './OrgTree.js';
import { TypeBadge } from './TypeBadge.js';
import { OrgUnitDialog, type OrgUnitFormValues } from './OrgUnitDialog.js';

/** Daraxtdagi tugunni yassi ro'yxatda topadi — tanlangan korxona ma'lumoti uchun. */
const findNode = (nodes: OrgUnitNode[], id: string): OrgUnitNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children, id);
    if (found !== null) return found;
  }
  return null;
};

interface FactProps {
  label: string;
  children: ReactNode;
}

/** Yorliq 12px/500 kulrang, qiymat 15px — o'qiladigani QIYMAT. */
const Fact = ({ label, children }: FactProps) => (
  <div className="flex min-w-0 flex-col gap-1">
    <dt className="text-xs font-medium text-text-muted">{label}</dt>
    <dd className="text-[15px] leading-6 text-text">{children}</dd>
  </div>
);

/**
 * Texnik qiymat — kod, identifikator.
 *
 * Chip ichida, chunki bunday qiymatni odam O'QIMAYDI: u uni nusxalaydi
 * yoki boshqa ekrandagisi bilan solishtiradi. Chegara uning qayerda
 * boshlanib qayerda tugashini aniq ko'rsatadi. Uzun kod o'raladi —
 * `BUXORO-VAGONLARNI-TEXNIK-KORIK-MARKAZI` bir qatorga sig'maydi.
 */
const CodeChip = ({ value }: { value: string }) => (
  <span
    className={cn(
      'tabular inline-block rounded-[var(--radius-sm)] bg-surface-2',
      'px-1.5 py-0.5 text-sm break-all text-text',
    )}
  >
    {value}
  </span>
);

/**
 * Manbasi hali ulanmagan ko'rsatkich.
 *
 * Ilgari bu yerda "—" va ostida "Manba ulanmagan" turardi, sahifa pastida
 * esa yana bir kulrang izoh qatori — bitta "hozircha yo'q" ma'nosi uchun
 * uchta element va ularning uchalasi ham XATOGA o'xshardi. Endi bitta
 * so'z va yonida savol belgisi: sababi ko'rsatkichning YONIDA, sichqoncha
 * yoki klaviatura bilan ochiladi.
 */
const PendingValue = () => (
  <span className="group relative inline-flex items-center gap-1.5">
    <span className="text-text-muted">{t('org.metricNotConnected')}</span>
    <button
      type="button"
      aria-label={t('org.metricPendingHint')}
      className={cn(
        'flex size-5 shrink-0 items-center justify-center rounded-[var(--radius-pill)]',
        'text-text-faint transition-colors duration-[var(--motion-fast)]',
        'ease-[var(--ease-standard)] hover:text-text',
      )}
    >
      <Info size={14} aria-hidden />
    </button>

    {/* Izoh TEPADA ochiladi: ikkala "ulanmagan" maydon panelning eng pastki
        qatorida turadi va pastga ochilgan izoh kartochka chetida kesilardi. */}
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute bottom-full left-0 z-[var(--z-popover)] mb-1.5',
        'w-56 rounded-[var(--radius-sm)] bg-primary px-2.5 py-1.5',
        'text-xs text-primary-contrast shadow-[var(--shadow-2)]',
        'opacity-0 transition-opacity duration-[var(--motion-fast)]',
        'ease-[var(--ease-standard)]',
        'group-hover:opacity-100 group-focus-within:opacity-100',
      )}
    >
      {t('org.metricPendingHint')}
    </span>
  </span>
);

/**
 * Tashkiliy tuzilma sahifasi: chapda daraxt, o'ngda tanlangan korxona.
 *
 * Daraxt bitta MTU ostida quriladi — backend `GET /org-units/tree` dan aynan
 * shuni talab qiladi va doira tekshiruvi ham shu ildizdan boshlanadi.
 */
export const OrgTreePage = () => {
  const { can } = usePermission();
  const toast = useToast();
  const queryClient = useQueryClient();
  const mtuFieldId = useId();

  const [mtuId, setMtuId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<OrgUnitDto | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const mtus = useQuery({
    queryKey: ['org', 'units', 'mtu'],
    queryFn: () => orgApi.listUnits({ type: 'MTU' }),
  });

  const activeMtuId = mtuId ?? mtus.data?.items[0]?.id ?? null;

  const tree = useQuery({
    queryKey: ['org', 'tree', activeMtuId],
    queryFn: () => orgApi.tree(activeMtuId ?? ''),
    enabled: activeMtuId !== null,
  });

  const nodes = useMemo(() => tree.data ?? [], [tree.data]);
  const selected = selectedId === null ? null : findNode(nodes, selectedId);

  /* Tor ekranda 340px daraxt + tafsilot yonma-yon sig'maydi, ikkalasi ham
     o'qilmay qolardi. Shuning uchun bir vaqtda BITTASI ko'rinadi va
     tanlangach tafsilotga o'tiladi — telefondagi ro'yxat/karta naqshi. */
  const wide = useMediaQuery('(min-width: 1024px)');
  const showTree = wide || selected === null;
  const showDetail = wide || selected !== null;

  const update = useMutation({
    mutationFn: (input: { id: string; values: OrgUnitFormValues }) =>
      orgApi.updateUnit(input.id, input.values),
    onSuccess: async () => {
      setEditing(null);
      setDialogError(null);
      await queryClient.invalidateQueries({ queryKey: ['org'] });
      toast.show(t('org.saved'), 'success');
    },
    onError: (error: unknown) => {
      setDialogError(error instanceof ApiClientError ? error.message : t('org.loadError'));
    },
  });

  const canEdit =
    selected !== null &&
    can('org.unit.manage', {
      mtuId: selected.mtuId ?? selected.id,
      orgUnitId: selected.id,
    });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t('org.title')}
        description={t('org.subtitle')}
        filters={
          /* Bu yerda ilgari XOM `<select>` turardi — umumiy komponent ham
             chetlab o'tilgan edi, ya'ni ilovadagi yagona joy bo'lib qolgandi. */
          mtus.data !== undefined && mtus.data.items.length > 1 ? (
            <Select
              variant="filter"
              id={mtuFieldId}
              label={t('org.mtuLabel')}
              value={activeMtuId ?? ''}
              onChange={(event) => {
                setMtuId(event.target.value);
                setSelectedId(null);
              }}
              options={mtus.data.items.map((unit) => ({ value: unit.id, label: unit.name }))}
            />
          ) : undefined
        }
      />

      {/*
        Ikki ustun: daraxt 340px sobit, tafsilot qolgan joyni oladi.
        Balandlik ekranga bog'lanadi va aylantirish PANEL ICHIDA bo'ladi —
        aks holda uzun daraxt sahifani cho'zib, o'ngdagi tafsilot ekrandan
        chiqib ketardi. Ayirma: topbar (4rem) + `main` to'ldirmasi (2.5rem)
        + sahifa sarlavhasi (~4rem) + bo'shliq (1.25rem).
      */}
      <div
        className={cn(
          'grid min-h-0 gap-5',
          'lg:h-[calc(100dvh-12rem)] lg:min-h-[26rem] lg:grid-cols-[21.25rem_minmax(0,1fr)]',
        )}
      >
        {showTree ? (
          <Card className="min-h-0 min-w-0" padded={false} bodyClassName="flex min-h-0 flex-col">
            {tree.isPending || mtus.isPending ? (
              <Skeleton className="m-4 h-64" />
            ) : tree.isError ? (
              <EmptyState
                title={t('org.loadError')}
                description={tree.error instanceof ApiClientError ? tree.error.message : undefined}
                action={
                  <Button variant="secondary" onClick={() => void tree.refetch()}>
                    {t('org.retry')}
                  </Button>
                }
              />
            ) : nodes.length === 0 ? (
              <EmptyState title={t('org.treeEmpty')} description={t('org.treeEmptyHint')} />
            ) : (
              <OrgTree
                nodes={nodes}
                selectedId={selectedId}
                onSelect={(node) => setSelectedId(node.id)}
              />
            )}
          </Card>
        ) : null}

        {!showDetail ? null : selected === null ? (
          <Card className="min-w-0 lg:self-start">
            <EmptyState
              icon={<Network size={24} aria-hidden />}
              title={t('org.selectUnit')}
              description={t('org.selectUnitHint')}
            />
          </Card>
        ) : (
          <div className="flex min-h-0 min-w-0 flex-col gap-3 lg:self-start lg:max-h-full">
            {/* Tor ekranda daraxt va tafsilot O'RIN ALMASHADI: ikkalasi
                yonma-yon 340px + kontent sig'maydi va ikkalasi ham
                o'qilmay qolardi. */}
            {wide ? null : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedId(null)}
                className="-ml-3 self-start"
              >
                <ArrowLeft size={14} aria-hidden />
                {t('org.backToTree')}
              </Button>
            )}

            <Card
              key={selected.id}
              className="motion-panel-in min-h-0 min-w-0"
              bodyClassName="overflow-y-auto"
              title={
                // Uzun nom KESILMAYDI — ikki qatorgacha ruxsat. Kartochkaning
                // odatiy sarlavhasi bir qatorga siqadi, korxona nomi esa
                // ba'zan 60 belgidan oshadi.
                <h2 className="line-clamp-2 text-xl font-semibold tracking-tight text-text">
                  {selected.name}
                </h2>
              }
              actions={
                canEdit ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setDialogError(null);
                      setEditing(selected);
                    }}
                  >
                    <Pencil size={16} aria-hidden />
                    {t('org.edit')}
                  </Button>
                ) : null
              }
            >
              {/* Maydonlar BITTA blok ichida: ilgari ular oq kartochkada
                  suzib turardi va pastida katta bo'sh maydon qolardi. Blok
                  kontent balandligicha bo'ladi, panel pastigacha cho'zilmaydi. */}
              <dl
                className={cn(
                  'grid gap-x-8 gap-y-6 rounded-[var(--radius-md)] bg-surface-2 p-4',
                  'sm:grid-cols-2 xl:grid-cols-3',
                )}
              >
                <Fact label={t('org.fieldCode')}>
                  <CodeChip value={selected.code} />
                </Fact>

                <Fact label={t('org.fieldType')}>
                  <TypeBadge type={selected.type} />
                </Fact>

                <Fact label={t('org.fieldMtu')}>
                  {selected.mtuId === null ? (
                    <PendingValue />
                  ) : (
                    <CodeChip value={selected.mtuId} />
                  )}
                </Fact>

                <Fact label={t('org.fieldParent')}>
                  {selected.parentId === null
                    ? '—'
                    : (findNode(nodes, selected.parentId)?.name ?? selected.parentId)}
                </Fact>

                <Fact label={t('org.fieldStatus')}>
                  <Badge tone={selected.isActive ? 'success' : 'neutral'}>
                    {selected.isActive ? t('org.statusActive') : t('org.statusInactive')}
                  </Badge>
                </Fact>

                <Fact label={t('org.metricWarehouses')}>
                  <PendingValue />
                </Fact>

                <Fact label={t('org.metricEmployees')}>
                  <PendingValue />
                </Fact>
              </dl>
            </Card>
          </div>
        )}
      </div>

      <OrgUnitDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        unit={editing}
        pending={update.isPending}
        error={dialogError}
        onSubmit={(values) => {
          if (editing !== null) update.mutate({ id: editing.id, values });
        }}
      />
    </div>
  );
};
